import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtDate, fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

export default function Logbook() {
  const { projectId, canWrite } = useProjectCtx();
  const canLog = canWrite("CONSTRUCTION");
  const [entries, setEntries] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [wbsItems, setWbsItems] = useState([]);
  const [filters, setFilters] = useState({ equipment_id: "__all__", start: "", end: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.equipment_id !== "__all__") params.equipment_id = filters.equipment_id;
      if (filters.start) params.start = filters.start;
      if (filters.end) params.end = filters.end;
      const [{ data: rows }, { data: eq }, { data: wbs }] = await Promise.all([
        api.get(`/projects/${projectId}/logbook`, { params }),
        api.get("/equipment"),
        api.get(`/projects/${projectId}/wbs`, { params: { include_groups: false } }),
      ]);
      setEntries(rows);
      setEquipment(eq);
      setWbsItems(wbs);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, filters]);

  useEffect(() => { load(); }, [load]);

  const equipmentLabel = (id) => {
    const eq = equipment.find((e) => e.id === id);
    return eq ? `${eq.code} — ${eq.name}` : "—";
  };
  const activityLabel = (id) => {
    const w = wbsItems.find((x) => x.id === id);
    return w ? `${w.code} — ${w.name}` : "—";
  };

  return (
    <div className="space-y-6" data-testid="logbook-page">
      <PageHeader
        title="Equipment Logbook"
        description="Daily hours/km/fuel run per equipment. Logging hours creates a cost entry and a manpower/machinery resource row against the linked activity."
        action={
          <div className="flex items-center gap-2">
            <Select value={filters.equipment_id} onValueChange={(v) => setFilters({ ...filters, equipment_id: v })}>
              <SelectTrigger className="w-48" data-testid="logbook-equipment-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All equipment</SelectItem>
                {equipment.map((eq) => <SelectItem key={eq.id} value={eq.id}>{eq.code} — {eq.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="w-36" data-testid="logbook-date-from-input" />
            <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="w-36" data-testid="logbook-date-to-input" />
            {canLog && <CreateLogbookEntryDialog projectId={projectId} equipment={equipment} wbsItems={wbsItems} onCreated={load} />}
          </div>
        }
      />

      <div className="rounded-xl border border-border bg-card">
        {!loading && entries.length === 0 ? (
          <EmptyState title="No logbook entries yet" description="Log daily hours against equipment and an activity." testId="logbook-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Date</TableHead><TableHead>Equipment</TableHead><TableHead>Activity</TableHead><TableHead className="text-right">Hours</TableHead><TableHead className="text-right">KM</TableHead><TableHead className="text-right">Fuel (L)</TableHead><TableHead>Operator</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id} data-testid={`logbook-row-${e.id}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(e.log_date)}</TableCell>
                  <TableCell className="text-xs text-foreground">{equipmentLabel(e.equipment_id)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.wbs_item_id ? activityLabel(e.wbs_item_id) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-foreground">{e.hours_run != null ? fmtNum(e.hours_run, 2) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{e.km_run != null ? fmtNum(e.km_run, 2) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{e.fuel_litres != null ? fmtNum(e.fuel_litres, 2) : "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.operator || "—"}</TableCell>
                  <TableCell className="text-right">{e.cost_entry_id && <Badge variant="secondary" data-testid={`logbook-cost-linked-badge-${e.id}`}>Costed</Badge>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateLogbookEntryDialog({ projectId, equipment, wbsItems, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ equipment_id: "", log_date: todayISO(), hours_run: "", km_run: "", fuel_litres: "", operator: "", wbs_item_id: "", remarks: "" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/logbook`, {
        ...form,
        hours_run: form.hours_run || null,
        km_run: form.km_run || null,
        fuel_litres: form.fuel_litres || null,
        wbs_item_id: form.wbs_item_id || null,
      });
      toast.success("Logbook entry recorded");
      setForm({ equipment_id: "", log_date: todayISO(), hours_run: "", km_run: "", fuel_litres: "", operator: "", wbs_item_id: "", remarks: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="create-logbook-entry-button"><Plus className="h-3.5 w-3.5" /> Log entry</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Logbook Entry</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-logbook-entry-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Equipment</Label>
              <Select required value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="logbook-equipment-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{equipment.map((eq) => <SelectItem key={eq.id} value={eq.id}>{eq.code} — {eq.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" value={form.log_date} onChange={set("log_date")} className="mt-1.5" data-testid="logbook-date-input" /></div>
          </div>
          <div>
            <Label>Activity (optional)</Label>
            <Select value={form.wbs_item_id || "__none__"} onValueChange={(v) => setForm({ ...form, wbs_item_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="logbook-activity-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not linked</SelectItem>
                {wbsItems.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Hours run</Label><Input type="number" step="0.5" value={form.hours_run} onChange={set("hours_run")} className="mt-1.5" data-testid="logbook-hours-input" /></div>
            <div><Label>KM run</Label><Input type="number" step="0.5" value={form.km_run} onChange={set("km_run")} className="mt-1.5" data-testid="logbook-km-input" /></div>
            <div><Label>Fuel (L)</Label><Input type="number" step="0.5" value={form.fuel_litres} onChange={set("fuel_litres")} className="mt-1.5" data-testid="logbook-fuel-input" /></div>
          </div>
          <div><Label>Operator</Label><Input value={form.operator} onChange={set("operator")} className="mt-1.5" data-testid="logbook-operator-input" /></div>
          <div><Label>Remarks</Label><Input value={form.remarks} onChange={set("remarks")} className="mt-1.5" data-testid="logbook-remarks-input" /></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-logbook-entry-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
